(() => {
  const sectionSelector = '[data-event-list]';
  const eventSelector = '[data-event-list-event]';

  const sectionsIn = (scope = document) => {
    const sections = Array.from(scope.querySelectorAll?.(sectionSelector) || []);

    if (scope.matches?.(sectionSelector)) sections.unshift(scope);

    return sections;
  };

  const parseIsoDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const parseTime = (value) => {
    const normalized = (value || '').trim().toLowerCase();
    const match = normalized.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?$/);

    if (!match) return Number.POSITIVE_INFINITY;

    let hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2] || '0', 10);
    const meridiem = (match[3] || '').replaceAll('.', '');

    if (meridiem === 'pm' && hours < 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return Number.POSITIVE_INFINITY;

    return (hours * 60) + minutes;
  };

  class EventList {
    constructor(section) {
      this.section = section;
      this.container = section.querySelector('[data-event-list-container]');
      this.loading = section.querySelector('[data-event-list-loading]');
      this.pagination = section.querySelector('[data-event-list-pagination]');
      this.heading = section.querySelector('[data-event-list-heading]');
      this.fetchUrl = section.dataset.eventListFetchUrl || '';
      this.today = section.dataset.eventListToday || '';
      this.locale = section.dataset.eventListLocale || undefined;
      this.limit = Number.parseInt(section.dataset.eventListLimit || '100', 10);
      this.pageSize = Number.parseInt(section.dataset.eventListPageSize || '25', 10);
      this.showQuantitySelector = section.dataset.eventListShowQuantity !== 'false';
      this.addToCartLabel = section.dataset.eventListAddLabel || '';
      this.designMode = section.dataset.eventListDesignMode === 'true';
      this.pageLabel = section.dataset.eventListPageLabel || 'Page';
      this.previousLabel = section.dataset.eventListPreviousLabel || 'Previous page';
      this.nextLabel = section.dataset.eventListNextLabel || 'Next page';
      this.currentPage = 1;
      this.cards = [];

      if (!this.container || !this.pagination) return;

      this.monthFormatter = new Intl.DateTimeFormat(this.locale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
      });
      this.shortMonthFormatter = new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        timeZone: 'UTC'
      });
      this.dayFormatter = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        timeZone: 'UTC'
      });

      this.loadEvents();
    }

    async loadEvents() {
      try {
        if (!this.fetchUrl) throw new Error('Event list URL is missing');

        const cards = [];
        const seenIds = new Set();
        const maxPages = Math.ceil(this.limit / 50);

        for (let page = 1; page <= maxPages && cards.length < this.limit; page += 1) {
          const responsePage = await this.fetchPage(page);

          responsePage.cards.forEach((card) => {
            const eventId = card.dataset.eventId || '';

            if (eventId && seenIds.has(eventId)) return;
            if (eventId) seenIds.add(eventId);
            cards.push(card);
          });

          if (!responsePage.hasNext) break;
        }

        this.cards = cards
          .filter((card) => card.dataset.eventDate >= this.today)
          .sort((a, b) => {
            const dateOrder = a.dataset.eventDate.localeCompare(b.dataset.eventDate);
            if (dateOrder !== 0) return dateOrder;

            const timeOrder = parseTime(a.dataset.eventStartTime) - parseTime(b.dataset.eventStartTime);
            if (Number.isFinite(timeOrder) && timeOrder !== 0) return timeOrder;
            if (parseTime(a.dataset.eventStartTime) !== parseTime(b.dataset.eventStartTime)) {
              return parseTime(a.dataset.eventStartTime) - parseTime(b.dataset.eventStartTime);
            }

            return (a.dataset.eventTitle || '').localeCompare(b.dataset.eventTitle || '', this.locale);
          })
          .slice(0, this.limit);

        if (this.cards.length === 0) {
          if (!this.designMode) this.section.hidden = true;
          return;
        }

        this.cards.forEach((card, index) => {
          window.InkhavenEventControlIds(card, this.section, index);
          this.applyEventControls(card);
        });
        this.section.hidden = false;
        this.renderPage(1, false);
      } catch (error) {
        if (!this.designMode) this.section.hidden = true;
      } finally {
        if (this.loading) this.loading.hidden = true;
      }
    }

    async fetchPage(page) {
      const url = new URL(this.fetchUrl, window.location.origin);
      url.searchParams.set('page', String(page));

      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Event list request failed: ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('[data-event-list-results]');

      return {
        cards: Array.from(results?.querySelectorAll(eventSelector) || []),
        hasNext: results?.dataset.eventListHasNext === 'true'
      };
    }

    renderPage(page, moveFocus = true) {
      const pageCount = Math.ceil(this.cards.length / this.pageSize);
      this.currentPage = Math.min(Math.max(page, 1), pageCount);
      const start = (this.currentPage - 1) * this.pageSize;
      const visibleCards = this.cards.slice(start, start + this.pageSize);
      const fragment = document.createDocumentFragment();
      let activeMonth = '';

      visibleCards.forEach((card) => {
        const eventDate = parseIsoDate(card.dataset.eventDate);
        const eventMonth = card.dataset.eventDate.slice(0, 7);

        if (eventMonth !== activeMonth && eventDate) {
          const monthHeading = document.createElement('h3');
          const monthText = document.createElement('span');

          monthHeading.className = 'event-list__month-heading';
          monthText.textContent = this.monthFormatter.format(eventDate);
          monthHeading.appendChild(monthText);
          fragment.appendChild(monthHeading);
          activeMonth = eventMonth;
        }

        this.localizeDate(card, eventDate);
        // Start current-page images now instead of waiting for scroll proximity.
        card.querySelectorAll('.event-list__image').forEach((image) => {
          image.loading = 'eager';
        });
        fragment.appendChild(card);
      });

      this.container.replaceChildren(fragment);
      this.renderPagination(pageCount);

      if (moveFocus) {
        const focusTarget = this.heading || this.container;
        focusTarget.focus({ preventScroll: true });
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    localizeDate(card, eventDate = parseIsoDate(card.dataset.eventDate)) {
      if (!eventDate) return;

      const month = card.querySelector('[data-event-list-month]');
      const day = card.querySelector('[data-event-list-day]');

      if (month) month.textContent = this.shortMonthFormatter.format(eventDate);
      if (day) day.textContent = this.dayFormatter.format(eventDate);
    }

    renderPagination(pageCount) {
      if (pageCount <= 1) {
        this.pagination.hidden = true;
        this.pagination.replaceChildren();
        return;
      }

      const fragment = document.createDocumentFragment();
      fragment.appendChild(this.createPageButton('‹', this.previousLabel, this.currentPage - 1, this.currentPage === 1));

      for (let page = 1; page <= pageCount; page += 1) {
        fragment.appendChild(this.createPageButton(String(page), `${this.pageLabel} ${page}`, page, false, page === this.currentPage));
      }

      fragment.appendChild(this.createPageButton('›', this.nextLabel, this.currentPage + 1, this.currentPage === pageCount));
      this.pagination.replaceChildren(fragment);
      this.pagination.hidden = false;
    }

    createPageButton(text, label, page, disabled = false, current = false) {
      const button = document.createElement('button');

      button.className = 'event-list__page-button';
      button.type = 'button';
      button.textContent = text;
      button.setAttribute('aria-label', label);
      button.disabled = disabled;

      if (current) button.setAttribute('aria-current', 'page');
      if (!disabled && !current) button.addEventListener('click', () => this.renderPage(page));

      return button;
    }

    applyEventControls(card) {
      const addButton = card.querySelector('[data-event-list-add-button]');
      const variantSelect = card.querySelector('[data-event-list-variant-select]');
      const quantityInput = card.querySelector('[data-event-list-quantity-input], input[name="quantity"]');

      if (this.addToCartLabel && addButton) {
        addButton.dataset.eventListDefaultAddLabel = this.addToCartLabel;
        addButton.dataset.eventListCartLabel = this.addToCartLabel;
      }

      if (variantSelect) {
        variantSelect.addEventListener('change', () => this.syncEventPurchaseControls(card));
      }

      if (quantityInput) {
        quantityInput.addEventListener('change', () => this.syncEventPurchaseControls(card));
      }

      if (!this.showQuantitySelector) {
        const quantityControl = card.querySelector('.event-list__quantity-control');
        const quantityLabel = card.querySelector('.event-list__quantity-label');
        const form = card.querySelector('.event-list__form');

        quantityControl?.remove();
        quantityLabel?.remove();

        if (form && !form.querySelector('input[name="quantity"]')) {
          const hiddenQuantity = document.createElement('input');
          hiddenQuantity.type = 'hidden';
          hiddenQuantity.name = 'quantity';
          hiddenQuantity.value = '1';
          hiddenQuantity.disabled = card.dataset.eventAvailable !== 'true';
          form.insertBefore(hiddenQuantity, addButton);
        }
      } else {
        card.querySelectorAll('[data-event-list-quantity-decrease], [data-event-list-quantity-increase]').forEach((button) => {
          button.addEventListener('click', () => {
            const control = button.closest('.event-list__quantity-control');
            const input = control?.querySelector('[data-event-list-quantity-input]');

            if (!input || input.disabled) return;

            const currentValue = Number.parseInt(input.value, 10) || 1;
            input.value = String(button.hasAttribute('data-event-list-quantity-increase')
              ? currentValue + 1
              : Math.max(1, currentValue - 1));
            this.syncEventPurchaseControls(card);
          });
        });
      }

      this.syncEventPurchaseControls(card);
    }

    syncEventPurchaseControls(card) {
      const form = card.querySelector('.event-list__form');
      const variantSelect = card.querySelector('[data-event-list-variant-select]');
      const variantInput = card.querySelector('[data-event-list-variant-input]');
      const selectedOption = variantSelect?.selectedOptions[0];
      const variantId = variantSelect ? variantSelect.value : variantInput?.value;
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const quantity = Math.max(Number.parseInt(quantityInput?.value, 10) || 1, 1);
      const isFree = selectedOption
        ? selectedOption.dataset.eventListFree === 'true'
        : card.dataset.eventSelectedFree === 'true';
      const isAvailable = selectedOption ? !selectedOption.disabled : card.dataset.eventAvailable === 'true';
      const price = card.querySelector('[data-event-list-price]');
      const rsvpButton = card.querySelector('[data-event-list-rsvp]');
      const addButton = card.querySelector('[data-event-list-add-button]');

      if (price && selectedOption?.dataset.eventListPriceText) {
        price.textContent = selectedOption.dataset.eventListPriceText;
      }

      if (rsvpButton) {
        rsvpButton.hidden = !isFree || !isAvailable || !variantId;

        if (variantId && rsvpButton.dataset.eventListRsvpBase) {
          rsvpButton.href = `${rsvpButton.dataset.eventListRsvpBase}${encodeURIComponent(variantId)}:${quantity}`;
        }
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        addButton.classList.toggle('button--primary', !isFree);
        addButton.classList.toggle('button--secondary', isFree);
        addButton.textContent = !isAvailable
          ? addButton.dataset.eventListSoldOutLabel
          : isFree
            ? addButton.dataset.eventListCartLabel
            : addButton.dataset.eventListDefaultAddLabel;
      }
    }
  }

  const initialize = (scope = document) => {
    sectionsIn(scope).forEach((section) => {
      if (section.hasAttribute('data-event-list-initialized')) return;

      section.setAttribute('data-event-list-initialized', 'true');
      new EventList(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
